import { useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { apiClient } from '@lib/api/client';

/**
 * Decode a JWT payload without verification (client-side only).
 * Returns the `exp` claim (seconds since epoch) or null.
 */
function getTokenExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * How far before expiration (in ms) should we trigger a refresh.
 * 2 minutes gives enough buffer for network latency.
 */
const REFRESH_BUFFER_MS = 2 * 60 * 1000;

/**
 * Minimum interval between refresh attempts to avoid hammering the server.
 */
const MIN_REFRESH_INTERVAL_MS = 30 * 1000;

/**
 * Proactively refreshes the JWT access token BEFORE it expires.
 *
 * Why: With a 30-minute access token, waiting for a 401 to trigger
 * refresh creates a visible delay on the first request after expiry.
 * This hook schedules a background refresh ~2 minutes before the token
 * dies, so the user never notices.
 *
 * It also handles the "laptop wake-up" scenario: when the tab becomes
 * visible again after being hidden (e.g. laptop lid closed), it checks
 * whether the token expired while the device was asleep and refreshes
 * immediately if needed.
 */
export function useProactiveRefresh() {
  const { isAuthenticated, accessToken } = useAuthStore();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRefreshRef = useRef<number>(0);

  const doRefresh = async () => {
    const now = Date.now();
    if (now - lastRefreshRef.current < MIN_REFRESH_INTERVAL_MS) return;
    lastRefreshRef.current = now;

    try {
      const { data } = await apiClient.post('/auth/refresh');
      localStorage.setItem('accessToken', data.accessToken);
      useAuthStore.setState({ accessToken: data.accessToken });
    } catch {
      // Refresh failed — the response interceptor in client.ts will
      // handle auth failure on the next real request. No action here.
    }
  };

  const scheduleRefresh = () => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const token = useAuthStore.getState().accessToken || localStorage.getItem('accessToken');
    if (!token) return;

    const exp = getTokenExp(token);
    if (!exp) return;

    const msUntilExpiry = exp * 1000 - Date.now();
    const msUntilRefresh = msUntilExpiry - REFRESH_BUFFER_MS;

    if (msUntilRefresh <= 0) {
      // Token already expired or about to — refresh now
      doRefresh().then(scheduleRefresh);
      return;
    }

    timerRef.current = setTimeout(() => {
      doRefresh().then(scheduleRefresh);
    }, msUntilRefresh);
  };

  // Schedule whenever the token changes (login or previous refresh)
  useEffect(() => {
    if (!isAuthenticated) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    scheduleRefresh();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle tab visibility change (laptop wake-up, tab switch back)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && useAuthStore.getState().isAuthenticated) {
        const token = useAuthStore.getState().accessToken || localStorage.getItem('accessToken');
        if (!token) return;

        const exp = getTokenExp(token);
        if (!exp) return;

        const msUntilExpiry = exp * 1000 - Date.now();
        if (msUntilExpiry < REFRESH_BUFFER_MS) {
          // Token expired or about to while tab was hidden — refresh now
          doRefresh().then(scheduleRefresh);
        } else {
          // Re-schedule in case the old timer was frozen by the browser
          scheduleRefresh();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
